import { useState } from 'react'
import { confirmSignUp, login, signUp, resendToken } from '../../../../api/auth'
import { useDispatch } from 'react-redux'
import { setAuthenticated } from '../../../../store/actions/authActions'
import Cookies from 'js-cookie'
import { EVENTS, trackEvent } from '../../../../events'
import { useNavigate, useLocation } from 'react-router-dom'
import { ROUTES } from '../../../../router/routes'
import {
    getStoredAppSumoData,
    linkPendingAppSumoLicense,
} from '../../../../utils/appsumo/AppSumoManager'
import { Server } from '../../../../api/Server'
import { useToast } from '../../../ui'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-“!@#%&\/,><\’:;|_~`])\S{8,99}$/

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

const useSignUp = (step, setStep, setErrors, invitationToken = null) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const toast = useToast()
    const [signUpUser, setSignUpUser] = useState({
        fullName: '',
        username: '',
        email: '',
        password: '',
        phone: '',
    })
    const [isLoading, setIsLoading] = useState(false)

    const handleSignUpInputChange = (fieldName, valueOrEvent) => {
        // Check if the input is from an event (likely from a checkbox)
        const isEvent = valueOrEvent && typeof valueOrEvent === 'object' && valueOrEvent.target

        if (isEvent) {
            const { target } = valueOrEvent
            const value = target.type === 'checkbox' ? target.checked : target.value
            setSignUpUser((prevState) => ({
                ...prevState,
                [fieldName]: value,
            }))
        } else {
            // This handles cases where the value is passed directly
            setSignUpUser((prevState) => ({
                ...prevState,
                [fieldName]: valueOrEvent,
            }))
        }
    }

    const validateSignUp = () => {
        let isValid = true
        if (signUpUser.fullName.length < 3) {
            setErrors((prevState) => ({
                ...prevState,
                fullName: 'Please enter your full name',
            }))
            isValid = false
        } else {
            setErrors((prevState) => ({
                ...prevState,
                fullName: '',
            }))
        }
        if (!signUpUser.email || !emailRegex.test(signUpUser.email)) {
            setErrors((prevState) => ({
                ...prevState,
                email: 'Please enter a valid business email',
            }))
            isValid = false
        } else {
            setErrors((prevState) => ({
                ...prevState,
                email: '',
            }))
        }
        if (!signUpUser.password || !passwordRegex.test(signUpUser.password)) {
            setErrors((prevState) => ({
                ...prevState,
                password:
                    'Please enter a password that is 8 characters long, contains both an uppercase and lowercase letter, a number, and a special character',
            }))
            isValid = false
        } else {
            setErrors((prevState) => ({
                ...prevState,
                password: '',
            }))
        }
        return isValid
    }

    const getHubspotUTK = () => {
        const cookie = Cookies.get('hubspotutk')
        return cookie ? cookie : ''
    }

    const handleSignUp = () => {
        signUpUser.username = generateUUID()
        trackEvent(EVENTS.ONBOARDING.SIGNUP_ATTEMPT, [
            { title: `Attempting new signup as ${signUpUser.email} ` },
        ])
        if (validateSignUp()) {
            setIsLoading(true)
            signUp({
                username: signUpUser.username,
                email: signUpUser.email,
                password: signUpUser.password,
                phone: signUpUser.phone,
            })
                .then(() => {
                    setStep(step + 1)
                })
                .catch((error) => {
                    if (
                        error.message.includes(
                            'An account with this email address already exists. Please sign in instead'
                        )
                    ) {
                        setErrors((prevState) => ({
                            ...prevState,
                            email: 'An account with this email address already exists. Please sign in instead.',
                        }))
                    } else if (
                        error.message.includes(
                            'An account with this email already exists but is not confirmed. Please check your email for the confirmation link.'
                        )
                    ) {
                        setErrors((prevState) => ({
                            ...prevState,
                            email: 'An account with this email already exists but is not confirmed. Please check your email for the confirmation link.',
                        }))
                    }
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }

    const handleConfirmSignup = async (verificationCode) => {
        setIsLoading(true)

        try {
            await confirmSignUp({
                username: signUpUser.username,
                code: verificationCode,
            })
        } catch (error) {
            // If it's NOT the "already confirmed" error, show error and stop
            if (!error.message?.includes('Current status is CONFIRMED')) {
                toast.error(error.message)
                setIsLoading(false)
                return
            }
            // Otherwise, continue (user already confirmed, which is fine)
        }

        // If we get here, either confirmation succeeded or user was already confirmed
        trackEvent(EVENTS.ONBOARDING.SIGNUP_CONFIRMED, [
            {
                title: `User ${signUpUser.email} confirmed signup account, now provisioning. `,
            },
        ])
        toast.success(
            'Account confirmed! Please wait a few moment while we provision a space for you.'
        )

        // provisionNewUser handles its own errors and loading state
        await provisionNewUser()
        try {
            window.dataLayer.push({ event: 'signup_confirmed' })
        } catch (e) {}
    }

    const getFirstName = () => {
        return signUpUser.fullName.split(' ')[0]
    }

    const getLastName = () => {
        return signUpUser.fullName.split(' ').slice(1).join(' ')
    }

    const provisionNewUser = async () => {
        setIsLoading(true)
        const pendingAppSumo = getStoredAppSumoData()
        const body = {
            userName: signUpUser.email,
            userID: signUpUser.username,
            firstName: getFirstName(),
            lastName: getLastName(),
            phoneNumber: signUpUser.phone,
            hubspotUTK: getHubspotUTK(),
            source: localStorage.getItem('mondayAccount')
                ? 'monday'
                : pendingAppSumo
                  ? 'appSumo'
                  : 'unknown',
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        try {
            const response = await fetch(
                'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/provisionnewuser',
                {
                    method: 'POST',
                    mode: 'cors',
                    cache: 'no-cache',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    redirect: 'follow',
                    referrerPolicy: 'no-referrer',
                    body: JSON.stringify(body),
                }
            )

            // Treat 504 timeout as success (provisioning may still be happening in background)
            if (response.status === 504) {
                setIsLoading(false)
                handleGoToCanvas()
                return
            }

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }

            if (pendingAppSumo && pendingAppSumo.license) {
                // We need to login first to get auth tokens, then link the license
                // This will be handled in handleGoToCanvas after login
                console.log('AppSumo license pending, will link after login')
            }

            // Only proceed to canvas if provisioning succeeded
            setIsLoading(false)
            handleGoToCanvas()
        } catch (error) {
            setIsLoading(false)
            toast.error(
                'There was an error creating your account. Please try again or contact support.'
            )
            // Do not proceed to canvas - user stays on verification screen
        }
    }

    const handleGoToCanvas = async () => {
        setIsLoading(true)
        try {
            const res = await login({
                username: signUpUser.email,
                password: signUpUser.password,
            })
            dispatch(setAuthenticated(true, res))

            // Store invitation token in localStorage if present (from URL or already in localStorage)
            const tokenToStore = invitationToken || localStorage.getItem('pendingInvitation')
            if (tokenToStore && !localStorage.getItem('pendingInvitation')) {
                localStorage.setItem('pendingInvitation', tokenToStore)
            }

            // Check for pending AppSumo license and link it
            const pendingAppSumo = getStoredAppSumoData()
            if (pendingAppSumo && pendingAppSumo.license) {
                toast.info('Activating your AppSumo license...')
                // Create server with auth data from login
                const server = new Server(
                    null,
                    res?.username,
                    res?.signInUserSession?.idToken?.jwtToken
                )
                const linkResult = await linkPendingAppSumoLicense(server)
                if (linkResult) {
                    if (linkResult.success) {
                        toast.success(linkResult.message)
                    } else {
                        toast.error(linkResult.message)
                    }
                }
            } else {
                toast.success('Loading new Workspace...')
            }

            // Navigate to onboarding after linking (or if no license to link)
            navigate(ROUTES.ONBOARDING)
        } catch (error) {
            toast.error(error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleResendCode = async () => {
        try {
            await resendToken({ username: signUpUser.username })
            toast.success('Verification code resent! Check your email.')
        } catch (error) {
            toast.error(error.message || 'Failed to resend code. Please try again.')
        }
    }

    return {
        signUpUser,
        handleSignUpInputChange,
        handleSignUp,
        isSignUpLoading: isLoading,
        handleConfirmSignup,
        handleGoToCanvas,
        handleResendCode,
    }
}

export default useSignUp
